import { PAYMENT_TARIFF } from '../types';
import { getPricesConfig } from './getPricesConfig';

/**
 * Single source of truth for the "given a tariff, what product/price/name should the paywall show?"
 * decision. Each tariff has a resolver that returns the product to dispatch into
 * `paymentModal.product`. Adding a new tariff = adding one entry below.
 *
 * Resolvers receive runtime context (currency/locale flags) so they stay pure and testable.
 */

export interface ResolveContext {
  /** '$' / '£' / '€' — derived from subscrUK/subscrEU URL params */
  currency: string;
  /** UK-specific copy switches some product names to a localized variant */
  subscrUK: boolean;
}

export interface ResolvedPayment {
  /** Stripe / catalog product id, stringified */
  productId: string;
  /** Strikethrough price shown next to the discounted price */
  oldPrice: string;
  /** Human-readable product description (e.g. "$19.99 per 1m intro, then $39.99 per month") */
  productName: string;
}

type Resolver = (ctx: ResolveContext) => ResolvedPayment;

const newDesign = (tariff: PAYMENT_TARIFF) => getPricesConfig(tariff).everydoggyNewDesign;

const secondPriceId = (tariff: PAYMENT_TARIFF) => String(newDesign(tariff).secondPriceConfig.id);
const firstPriceId = (tariff: PAYMENT_TARIFF) => String(newDesign(tariff).firstPriceConfig.id);
const firstPriceDefaultOldPrice = (tariff: PAYMENT_TARIFF) =>
  String(newDesign(tariff).firstPriceConfig.defaultOldPrices);

const TARIFF_PAYMENT_RESOLVERS: Partial<Record<PAYMENT_TARIFF, Resolver>> = {
  // --- Regular (full-price) tariffs ---
  [PAYMENT_TARIFF.V21]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V21),
    oldPrice: '62.82',
    productName: '$19.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V27]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V27),
    oldPrice: '55.29',
    productName: '$25.99 per 3m intro, then $79.99 per 3 months',
  }),
  [PAYMENT_TARIFF.V29]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V29),
    oldPrice: '62.82',
    productName: '$14.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V31]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V31),
    oldPrice: '62.82',
    productName: '$11.99 per 1m intro, then $49.99 per month',
  }),
  [PAYMENT_TARIFF.V33]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V33),
    oldPrice: '62.82',
    productName: '$11.99 per 1m intro, then $59.99 per month',
  }),
  [PAYMENT_TARIFF.V35]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V35),
    oldPrice: '25.51',
    productName: '$11.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V37]: ({ currency, subscrUK }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V37),
    oldPrice: '14.87',
    productName: subscrUK
      ? `${currency}11.99 per 1m intro, then ${currency}49.99 per month`
      : '$6.99 per 1m intro, then $49.99 per month',
  }),
  [PAYMENT_TARIFF.V39]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V39),
    oldPrice: '62.82',
    productName: '$11.99 per 1m intro, then $29.99 per 2 weeks',
  }),
  [PAYMENT_TARIFF.V41]: ({ currency }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V41),
    oldPrice: '62.82',
    productName: `${currency}6.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
  }),
  [PAYMENT_TARIFF.V43]: ({ currency }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V43),
    oldPrice: '39.99',
    productName: `${currency}14.99 per 1m intro, then ${currency}39.99 per month`,
  }),
  [PAYMENT_TARIFF.V45]: ({ currency }) => ({
    productId: firstPriceId(PAYMENT_TARIFF.V45),
    oldPrice: '39.99',
    productName: `${currency}14.99 per 1m intro, then ${currency}39.99 per month`,
  }),
  [PAYMENT_TARIFF.V47]: ({ currency }) => ({
    productId: firstPriceId(PAYMENT_TARIFF.V47),
    oldPrice: firstPriceDefaultOldPrice(PAYMENT_TARIFF.V47),
    productName: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
  }),
  [PAYMENT_TARIFF.V48]: ({ currency }) => ({
    productId: firstPriceId(PAYMENT_TARIFF.V48),
    oldPrice: firstPriceDefaultOldPrice(PAYMENT_TARIFF.V48),
    productName: `${currency}11.99 per 1m intro, then ${currency}39.99 per 2 weeks`,
  }),
  [PAYMENT_TARIFF.V49]: ({ currency }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V49),
    oldPrice: '62.82',
    productName: `${currency}6.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
  }),
  [PAYMENT_TARIFF.V001]: ({ currency }) => ({
    productId: firstPriceId(PAYMENT_TARIFF.V001),
    oldPrice: firstPriceDefaultOldPrice(PAYMENT_TARIFF.V001),
    productName: `${currency}9.99 per 1 day`,
  }),
  // Always € — this tariff is the EUR-priced product regardless of ?subscrEU
  [PAYMENT_TARIFF.V002]: () => ({
    productId: firstPriceId(PAYMENT_TARIFF.V002),
    oldPrice: firstPriceDefaultOldPrice(PAYMENT_TARIFF.V002),
    productName: '€9.99 per 1 day',
  }),
  // Always £ — this tariff is the GBP-priced product regardless of ?subscrUK
  [PAYMENT_TARIFF.V003]: () => ({
    productId: firstPriceId(PAYMENT_TARIFF.V003),
    oldPrice: firstPriceDefaultOldPrice(PAYMENT_TARIFF.V003),
    productName: '£9.99 per 1 day',
  }),

  // --- Discount / special-offer tariffs (after user declines the regular offer) ---
  [PAYMENT_TARIFF.V22]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V22),
    oldPrice: '62.82',
    productName: '$14.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V28]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V28),
    oldPrice: '55.29',
    productName: '$19.99 per 3m intro, then $79.99 per 3 months',
  }),
  [PAYMENT_TARIFF.V30]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V30),
    oldPrice: '62.82',
    productName: '$11.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V32]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V32),
    oldPrice: '62.82',
    productName: '$8.99 per 1m intro, then $49.99 per month',
  }),
  [PAYMENT_TARIFF.V34]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V34),
    oldPrice: '62.82',
    productName: '$8.99 per 1m intro, then $59.99 per month',
  }),
  [PAYMENT_TARIFF.V36]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V36),
    oldPrice: '25.51',
    productName: '$8.99 per 1m intro, then $39.99 per month',
  }),
  [PAYMENT_TARIFF.V38]: ({ currency, subscrUK }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V38),
    oldPrice: '14.87',
    productName: subscrUK
      ? `${currency}8.99 per 1m intro, then ${currency}49.99 per month`
      : '$3.99 per 1m intro, then $49.99 per month',
  }),
  [PAYMENT_TARIFF.V40]: () => ({
    productId: secondPriceId(PAYMENT_TARIFF.V40),
    oldPrice: '62.82',
    productName: '$8.99 per 1m intro, then $29.99 per 2 weeks',
  }),
  [PAYMENT_TARIFF.V42]: ({ currency }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V42),
    oldPrice: '62.82',
    productName: `${currency}3.99 per 1m intro, then ${currency}29.99 per 2 weeks`,
  }),
  [PAYMENT_TARIFF.V44]: ({ currency }) => ({
    productId: secondPriceId(PAYMENT_TARIFF.V44),
    oldPrice: '62.82',
    productName: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
  }),
  [PAYMENT_TARIFF.V46]: ({ currency }) => ({
    productId: firstPriceId(PAYMENT_TARIFF.V46),
    oldPrice: '62.82',
    productName: `${currency}11.99 per 1m intro, then ${currency}39.99 per month`,
  }),
};

/**
 * Returns the product config for a tariff, or `null` if the tariff has no mapping.
 * Callers should treat null as "leave the existing product as-is".
 */
export function resolveTariffPayment(
  tariff: string | null | undefined,
  ctx: ResolveContext
): ResolvedPayment | null {
  if (!tariff) return null;
  const resolver = TARIFF_PAYMENT_RESOLVERS[tariff as PAYMENT_TARIFF];
  return resolver ? resolver(ctx) : null;
}
